import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { MessageSquare, MoreHorizontal, Trash2 } from "lucide-react";
import { motion } from "motion/react";
import { useState } from "react";
import {
  canvasToolbarButtonVariants,
  canvasToolbarDividerVariants,
  canvasToolbarShellVariants,
} from "../../../styles/canvas-toolbar-styles";
import type { ContextMenuAction } from "./BaseCard";

export interface BaseEntityCardFloatingToolbarProps {
  visible: boolean;
  entityActions?: ContextMenuAction[];
  utilityActions?: ContextMenuAction[];
  onChatClick: () => void;
  onDeleteClick?: () => void;
  /** Opens the node's full context menu (mirrors right-click). Renders a trailing three-dot button. */
  onMoreClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export function BaseEntityCardFloatingToolbar({
  visible,
  entityActions = [],
  utilityActions = [],
  onChatClick,
  onDeleteClick,
  onMoreClick,
}: BaseEntityCardFloatingToolbarProps) {
  const visibleEntityActions = entityActions.slice(0, 2);
  const overflowEntityActions = entityActions.slice(2);

  const [overflowOpen, setOverflowOpen] = useState(false);
  const [openPopoverKey, setOpenPopoverKey] = useState<string | null>(null);

  if (!visible) return null;

  return (
    <TooltipProvider delayDuration={300}>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 8 }}
        transition={{ duration: 0.15, ease: "easeOut" }}
        className="mx-auto w-fit"
      >
        <div
          className={canvasToolbarShellVariants({ orientation: "horizontal" })}
        >
          {visibleEntityActions.map((action) => (
            <Tooltip key={action.key}>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick();
                  }}
                  disabled={action.disabled}
                  className={canvasToolbarButtonVariants({ accent: true })}
                >
                  <div className="size-4">{action.icon}</div>
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">{action.label}</TooltipContent>
            </Tooltip>
          ))}

          {overflowEntityActions.length > 0 && (
            <Popover open={overflowOpen} onOpenChange={setOverflowOpen}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <button
                      onClick={(e) => e.stopPropagation()}
                      className={canvasToolbarButtonVariants({ accent: true })}
                    >
                      <MoreHorizontal className="size-4" />
                    </button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent side="top">More actions</TooltipContent>
              </Tooltip>
              <PopoverContent
                className="w-48 p-1"
                align="center"
                side="top"
                sideOffset={8}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                {overflowEntityActions.map((action) => (
                  <button
                    key={action.key}
                    onClick={() => {
                      action.onClick();
                      setOverflowOpen(false);
                    }}
                    disabled={action.disabled}
                    className={cn(
                      "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-n-10",
                      "disabled:cursor-not-allowed disabled:opacity-50",
                      action.destructive && "text-lc-blocked",
                    )}
                  >
                    <span className="h-4 w-4 shrink-0">{action.icon}</span>
                    {action.label}
                  </button>
                ))}
              </PopoverContent>
            </Popover>
          )}

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onChatClick();
                }}
                className={canvasToolbarButtonVariants({ accent: true })}
              >
                <MessageSquare className="size-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">Chat about this</TooltipContent>
          </Tooltip>

          {utilityActions.length > 0 && (
            <div
              className={canvasToolbarDividerVariants({
                orientation: "horizontal",
              })}
              aria-hidden
            />
          )}

          {utilityActions.map((action) =>
            action.dropdownItems && action.dropdownItems.length > 0 ? (
              <Popover
                key={action.key}
                open={openPopoverKey === action.key}
                onOpenChange={(open) =>
                  setOpenPopoverKey(open ? action.key : null)
                }
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                      <button
                        onClick={(e) => e.stopPropagation()}
                        disabled={action.disabled}
                        className={canvasToolbarButtonVariants()}
                      >
                        <div className="size-4">{action.icon}</div>
                      </button>
                    </PopoverTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="top">{action.label}</TooltipContent>
                </Tooltip>
                <PopoverContent
                  className="w-48 p-1"
                  align="center"
                  side="top"
                  sideOffset={8}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  {action.dropdownItems.map((item) => (
                    <button
                      key={item.key}
                      onClick={() => {
                        item.onClick();
                        setOpenPopoverKey(null);
                      }}
                      className="w-full rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-n-10"
                    >
                      {item.label}
                    </button>
                  ))}
                </PopoverContent>
              </Popover>
            ) : (
              <Tooltip key={action.key}>
                <TooltipTrigger asChild>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      action.onClick();
                    }}
                    disabled={action.disabled}
                    className={canvasToolbarButtonVariants()}
                  >
                    <div className="size-4">{action.icon}</div>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">{action.label}</TooltipContent>
              </Tooltip>
            ),
          )}

          {onDeleteClick && (
            <>
              <div
                className={canvasToolbarDividerVariants({
                  orientation: "horizontal",
                })}
                aria-hidden
              />
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteClick();
                    }}
                    className={canvasToolbarButtonVariants({
                      destructive: true,
                    })}
                  >
                    <Trash2 className="size-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">Delete</TooltipContent>
              </Tooltip>
            </>
          )}

          {onMoreClick && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoreClick(e);
                  }}
                  aria-label="More actions"
                  className={canvasToolbarButtonVariants()}
                >
                  <MoreHorizontal className="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">More</TooltipContent>
            </Tooltip>
          )}
        </div>
      </motion.div>
    </TooltipProvider>
  );
}
